import {
  InfoConfigDifference,
  InfoConfigKind,
  InfoConfigProblem,
  InfoConfigVersionRow,
} from "@/data/models/InfoConfigAdministration";

/**
 * Beschreibungen in the admin portal: which versions a filter shows, how a version is badged, the next
 * version to suggest, and how a check's findings are grouped. Pure. IRES Cloud repeats every rule that
 * decides anything (`checkInfoConfigUploadForAdministration`, `publish…`).
 */

/** A new version: digits, a dot and a two-digit minor, as IRES Cloud requires. */
export const VERSION_PATTERN = /^\d+\.\d{2}$/;

/** IRES Cloud's limit for a Beschreibung file. */
export const MAX_INFO_CONFIG_BYTES = 2 * 1024 * 1024;

export type InfoConfigFilter = "CURRENT" | "TARGETS" | "RETIRED" | "LEGACY";

export const INFO_CONFIG_FILTERS: InfoConfigFilter[] = ["CURRENT", "TARGETS", "RETIRED", "LEGACY"];

/** A row of the list, with the kind it belongs to. */
export interface InfoConfigListRow extends InfoConfigVersionRow {
  kind: InfoConfigKind;
}

/** Whether anything takes a version as its target: the fleet, a client or a client's test users. */
export function isTarget(row: InfoConfigVersionRow): boolean {
  return row.isGlobalTarget || row.clientTargetCount > 0 || row.testTargetCount > 0;
}

/**
 * The rows a filter shows: current room versions (not retired), the targets among them, the retired
 * ones, or the legacy leak-detection files.
 */
export function filterInfoConfigRows(
  room: InfoConfigVersionRow[],
  leakDetection: InfoConfigVersionRow[],
  filter: InfoConfigFilter
): InfoConfigListRow[] {
  if (filter === "LEGACY") {
    return leakDetection.map((row) => ({ ...row, kind: "LEAK_DETECTION" as const }));
  }

  const rows = room.map((row) => ({ ...row, kind: "ROOM" as const }));
  switch (filter) {
    case "TARGETS":
      return rows.filter(isTarget);
    case "RETIRED":
      return rows.filter((row) => row.retired);
    default:
      return rows.filter((row) => !row.retired);
  }
}

/** One badge of a version: an i18n key and its count, if it carries one. */
export interface InfoConfigBadge {
  key: string;
  count?: number;
  /** A badge that warns rather than informs. */
  warning?: boolean;
  /** The global target, the version the whole fleet follows: drawn filled, so it stands out (2026-11-02). */
  highlight?: boolean;
}

/** What a version is to the fleet: global target, client and test targets, retired, unlisted, missing. */
export function versionBadges(row: InfoConfigVersionRow): InfoConfigBadge[] {
  const badge = (name: string, extra: Partial<InfoConfigBadge> = {}) => ({ key: `admin.infoConfig.badges.${name}`, ...extra });

  return [
    ...(row.isGlobalTarget ? [badge("globalTarget", { highlight: true })] : []),
    ...(row.clientTargetCount > 0 ? [badge("clientTargets", { count: row.clientTargetCount })] : []),
    ...(row.testTargetCount > 0 ? [badge("testTargets", { count: row.testTargetCount })] : []),
    ...(row.retired ? [badge("retired")] : []),
    ...(row.hasFile && !row.listed && !row.retired ? [badge("unlisted")] : []),
    ...(!row.hasFile ? [badge("noFile", { warning: true })] : []),
  ];
}

/** Versions compared as IRES Project compares them: the minor padded on the right (`5.5` is `5.50`). */
export function compareVersions(a: string, b: string): number {
  const partsA = a.split(".");
  const partsB = b.split(".");

  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    let left = partsA[i] ?? "0";
    let right = partsB[i] ?? "0";
    if (i > 0) {
      const width = Math.max(left.length, right.length);
      left = left.padEnd(width, "0");
      right = right.padEnd(width, "0");
    }
    const numberLeft = /^\d+$/.test(left) ? Number(left) : -1;
    const numberRight = /^\d+$/.test(right) ? Number(right) : -1;
    if (numberLeft !== numberRight) {
      return numberLeft - numberRight;
    }
  }
  return 0;
}

/** The newest version in the two-digit format, or null. */
export function newestVersion(rows: InfoConfigVersionRow[]): string | null {
  return (
    rows
      .map((row) => row.version)
      .filter((version) => VERSION_PATTERN.test(version))
      .sort((a, b) => compareVersions(b, a))[0] ?? null
  );
}

/** The version a new upload most likely is: one above the newest (`5.57` → `5.58`, `5.99` → `6.00`). */
export function suggestNextVersion(rows: InfoConfigVersionRow[]): string {
  const newest = newestVersion(rows);
  if (newest === null) {
    return "1.00";
  }
  const [major, minor] = newest.split(".").map(Number);
  return minor >= 99 ? `${major + 1}.00` : `${major}.${String(minor + 1).padStart(2, "0")}`;
}

/** The version a file name carries (`info.5.58.csv` → `5.58`), or null. */
export function versionFromFileName(fileName: string): string | null {
  const match = /^info\.(.+)\.csv$/i.exec(fileName.trim());
  return match ? match[1] : null;
}

/** What is wrong with a typed version before IRES Cloud is asked: format, used, or not above the newest. */
export function versionInputProblem(version: string, rows: InfoConfigVersionRow[]): "FORMAT" | "TAKEN" | "NOT_NEWER" | null {
  if (!VERSION_PATTERN.test(version.trim())) {
    return "FORMAT";
  }
  if (rows.some((row) => row.version === version.trim())) {
    return "TAKEN";
  }
  const newest = newestVersion(rows);
  return newest !== null && compareVersions(version.trim(), newest) <= 0 ? "NOT_NEWER" : null;
}

/** The problems by severity. */
export function splitProblems(problems: InfoConfigProblem[]): { errors: InfoConfigProblem[]; warnings: InfoConfigProblem[] } {
  return {
    errors: problems.filter((problem) => problem.severity === "ERROR"),
    warnings: problems.filter((problem) => problem.severity === "WARNING"),
  };
}

/** The differences by section, in the order they come, and how many of each kind. */
export function groupDifferences(differences: InfoConfigDifference[]): {
  sections: Array<{ section: string; items: InfoConfigDifference[] }>;
  optionChanges: number;
} {
  const sections: Array<{ section: string; items: InfoConfigDifference[] }> = [];

  for (const difference of differences) {
    let group = sections.find((entry) => entry.section === difference.section);
    if (!group) {
      group = { section: difference.section, items: [] };
      sections.push(group);
    }
    group.items.push(difference);
  }

  return { sections, optionChanges: differences.filter((difference) => difference.kind === "OPTION_CHANGED").length };
}

/** The snackbar message for a refused or failed call, by IRES Cloud's reason. */
export function infoConfigErrorKey(error: unknown): string {
  const failure = (error ?? {}) as { code?: unknown; details?: { reason?: unknown } | null };
  const reason = typeof failure.details?.reason === "string" ? failure.details.reason : "";
  const known = [
    "CHECK_FAILED",
    "WARNINGS_NOT_ACKNOWLEDGED",
    "VERSION_TAKEN",
    "VERSION_RETIRED",
    "VERSION_IS_TARGET",
    "NEEDS_SYSADMIN",
    "NO_FILE",
    "FILE_TOO_LARGE",
  ];
  return known.includes(reason) ? `admin.infoConfig.errors.${reason}` : "admin.infoConfig.errors.FAILED";
}
